import { useCallback } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import { SET_DEFAULT_NUMBER_FORMAT } from '../../constants';
import { ClientError } from '../errors';
import { requestDefaultFormats } from '../requests/requestDefaultFormats';

// A database without /defaultFormats answers 4xx; remember that for the session
// so the failed request is not repeated on every dataset definition load.
let endpointUnavailable = false;

export const useDefaultNumberFormat = () => {
  const {
    dispatch,
    state: { token, defaultNumberFormat },
  } = useUserState();

  // Returns null when the format cannot be obtained — callers then leave the
  // definition untouched, which keeps the current, unformatted rendering.
  const resolveDefaultNumberFormat = useCallback(
    async (forceHeader?: string): Promise<string | null> => {
      if (defaultNumberFormat) return defaultNumberFormat;
      if (endpointUnavailable) return null;

      try {
        const res = await requestDefaultFormats(token, forceHeader);
        if (!res?.number) return null;
        dispatch({ type: SET_DEFAULT_NUMBER_FORMAT, payload: res.number });
        return res.number;
      } catch (error) {
        // Only a client error means the endpoint is missing; server errors and
        // timeouts stay retryable. Never surfaced to the user — a missing
        // formatting default must not block the dataset.
        if (error instanceof ClientError) endpointUnavailable = true;
        console.error('Failed to fetch default formats', error);
        return null;
      }
    },
    [defaultNumberFormat, dispatch, token]
  );

  return { defaultNumberFormat, resolveDefaultNumberFormat };
};
